import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { warehouseAPI } from "../../services/api";
import { Package, Wrench, Warehouse as WarehouseIcon, ArrowLeftRight, BarChart3, Settings, TrendingDown, AlertTriangle, PackageCheck, Receipt } from "lucide-react";
import GoodsReceiptPage from "./GoodsReceiptPage";
import CustomerReceiptPage from "./CustomerReceiptPage";
import ProductsPage from "./ProductsPage";
import ServicesPage from "./ServicesPage";
import WarehousesPage from "./WarehousesPage";
import TransactionsPage from "./TransactionsPage";
import TransfersPage from "./TransfersPage";
import ReportsPage from "./ReportsPage";
import SettingsPage from "./SettingsPage";

type Tab = "dashboard" | "goods_receipt" | "customer_receipt" | "products" | "services" | "warehouses" | "transactions" | "transfers" | "reports" | "settings";

const fmt = (n: any) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

export default function Warehouse() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [tab, setTab]         = useState<Tab>("dashboard");
  const [stats, setStats]     = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (tab === "dashboard") loadDashboard(); }, [tab]);

  const loadDashboard = async () => {
    setLoading(true);
    try { const r = await warehouseAPI.dashboard(); if (r.success) setStats(r); }
    catch {}
    setLoading(false);
  };

  const tabs = [
    { id: "dashboard",     label: "داشبورد",       icon: BarChart3 },
    { id: "goods_receipt", label: "رسید جنس",      icon: PackageCheck },
    { id: "customer_receipt", label: "رسید مشتری", icon: Receipt },
    { id: "products",      label: "اجناس",          icon: Package },
    { id: "services",     label: "خدمات",          icon: Wrench },
    { id: "warehouses",   label: "گدام‌ها",         icon: WarehouseIcon },
    { id: "transactions", label: "ورود/خروج",       icon: TrendingDown },
    { id: "transfers",    label: "انتقالات",        icon: ArrowLeftRight },
    { id: "reports",      label: "گزارشات",         icon: BarChart3 },
    { id: "settings",     label: "تنظیمات",         icon: Settings },
  ];

  const color = "#10b981";

  return (
    <div style={{ padding: "24px 28px 48px", fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }} dir="rtl">
      {/* هدر */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color, fontWeight: 700, letterSpacing: "1.5px", marginBottom: 6 }}>📦 گدام</div>
        <h1 style={{ fontSize: 26, fontWeight: 900, margin: 0, color: T.text }}>مدیریت گدام</h1>
        <p style={{ fontSize: 13, color: T.subtext, margin: "5px 0 0" }}>اجناس، خدمات، گدام‌ها، ورود/خروج و گزارشات</p>
      </div>

      {/* تب‌ها */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28, borderBottom: `2px solid ${T.cardBorder}`, overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id as Tab)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", color: tab === t.id ? color : T.subtext, borderBottom: tab === t.id ? `2px solid ${color}` : "2px solid transparent", marginBottom: -2, whiteSpace: "nowrap", transition: "all 0.2s" }}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* داشبورد */}
      {tab === "dashboard" && (
        loading ? (
          <div style={{ textAlign: "center", padding: 80, color: T.subtext }}>
            <div style={{ width: 40, height: 40, border: `3px solid ${color}30`, borderTop: `3px solid ${color}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 16px" }} />
            در حال بارگذاری...
          </div>
        ) : (
          <div>
            {/* کارت‌های آمار */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 14, marginBottom: 24 }}>
              {[
                { label: "کل اجناس",       val: fmt(stats?.stats?.products),       color: "#10b981", icon: Package,         click: () => setTab("products") },
                { label: "کل خدمات",       val: fmt(stats?.stats?.services),       color: "#3b82f6", icon: Wrench,          click: () => setTab("services") },
                { label: "گدام‌های فعال",   val: fmt(stats?.stats?.warehouses),     color: "#8b5cf6", icon: WarehouseIcon,   click: () => setTab("warehouses") },
                { label: "کم‌موجودی",       val: fmt(stats?.stats?.low_stock),      color: "#f59e0b", icon: AlertTriangle,   click: () => setTab("products") },
                { label: "موجودی منفی",     val: fmt(stats?.stats?.negative_stock), color: "#ef4444", icon: TrendingDown,    click: () => setTab("products") },
              ].map((card, i) => (
                <div key={i} onClick={card.click} style={{ borderRadius: 14, padding: 18, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}`, borderTop: `3px solid ${card.color}`, cursor: "pointer", transition: "transform 0.1s" }}
                  onMouseEnter={e => (e.currentTarget.style.transform = "translateY(-2px)")}
                  onMouseLeave={e => (e.currentTarget.style.transform = "translateY(0)")}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 34, height: 34, borderRadius: 9, background: `${card.color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <card.icon size={16} color={card.color} />
                    </div>
                    <div style={{ fontSize: 11, color: T.subtext, fontWeight: 600 }}>{card.label}</div>
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: card.color }}>{card.val}</div>
                </div>
              ))}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {/* آخرین تراکنش‌ها */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>📋 آخرین ورود/خروج</div>
                {(stats?.recent_transactions || []).length === 0
                  ? <div style={{ color: T.subtext, textAlign: "center", padding: 20 }}>هیچ تراکنشی ثبت نشده</div>
                  : (stats?.recent_transactions || []).map((tx: any, i: number) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 8px", borderBottom: `1px solid ${T.cardBorder}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 7, background: tx.type === "in" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>
                          {tx.type === "in" ? "⬇️" : "⬆️"}
                        </div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{tx.product_name}</div>
                          <div style={{ fontSize: 10, color: T.subtext }}>{tx.warehouse_name}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 13, fontWeight: 800, color: tx.type === "in" ? "#10b981" : "#ef4444" }}>
                          {tx.type === "in" ? "+" : "-"}{fmt(tx.quantity)}
                        </div>
                        <div style={{ fontSize: 10, color: T.subtext }}>{tx.date}</div>
                      </div>
                    </div>
                  ))
                }
              </div>

              {/* آخرین انتقالات */}
              <div style={{ borderRadius: 16, padding: 20, background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${T.cardBorder}` }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>🔄 آخرین انتقالات</div>
                {(stats?.recent_transfers || []).length === 0
                  ? <div style={{ color: T.subtext, textAlign: "center", padding: 20 }}>هیچ انتقالی ثبت نشده</div>
                  : (stats?.recent_transfers || []).map((tr: any, i: number) => (
                    <div key={i} style={{ padding: "9px 8px", borderBottom: `1px solid ${T.cardBorder}` }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{tr.product_name}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                        <span style={{ fontSize: 11, color: "#ef4444", background: "rgba(239,68,68,0.1)", padding: "2px 8px", borderRadius: 5 }}>{tr.from_name}</span>
                        <ArrowLeftRight size={11} color={T.subtext} />
                        <span style={{ fontSize: 11, color: "#10b981", background: "rgba(16,185,129,0.1)", padding: "2px 8px", borderRadius: 5 }}>{tr.to_name}</span>
                        <span style={{ fontSize: 11, color: T.subtext, marginRight: "auto" }}>{fmt(tr.quantity)}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          </div>
        )
      )}

      {tab === "goods_receipt" && <GoodsReceiptPage />}
      {tab === "customer_receipt" && <CustomerReceiptPage />}
      {tab === "products"     && <ProductsPage />}
      {tab === "services"     && <ServicesPage />}
      {tab === "warehouses"   && <WarehousesPage />}
      {tab === "transactions" && <TransactionsPage />}
      {tab === "transfers"    && <TransfersPage />}
      {tab === "reports"      && <ReportsPage />}
      {tab === "settings"     && <SettingsPage />}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
